export interface LocationInfo {
  country_code: string;
  country_name: string;
  city: string;
  region: string;
  latitude: number;
  longitude: number;
  timezone: string;
  currency: string;
}

// Fallback location data when IP geolocation is blocked
const getFallbackLocation = (): LocationInfo => {
  return {
    country_code: "HK",
    country_name: "Hong Kong S.A.R.",
    city: "Hong Kong",
    region: "Hong Kong Island",
    latitude: 22.3193,
    longitude: 114.1694,
    timezone: "Asia/Hong_Kong",
    currency: "HKD",
  };
};

/** A user's IP does not move within a tab session — detect once, then reuse. */
const LOCATION_CACHE_KEY = "meed_detected_location_v1";
/** Stop waiting on a single geo service so one slow host cannot stall the chain. */
const SERVICE_TIMEOUT_MS = 4000;

/**
 * IP geolocation services in the order they are tried.
 *
 * ipinfo.io is first because it is the one that actually answers: ipapi.co's
 * free tier rate-limits to 429 under normal portal traffic. ipgeolocation.io is
 * only included when a real key is configured — the previous hardcoded
 * `apiKey=free` is not a key and always returned 401, so every user paid for a
 * guaranteed-failed round trip before reaching a working service.
 * @returns {string[]} Service URLs, in the order they should be tried.
 */
const getServiceUrls = (): string[] => {
  const ipGeolocationKey =
    process.env.REACT_APP_IPGEOLOCATION_API_KEY?.trim() ?? "";
  return [
    "https://ipinfo.io/json",
    "https://ipapi.co/json/",
    ...(ipGeolocationKey
      ? [`https://api.ipgeolocation.io/ipgeo?apiKey=${ipGeolocationKey}`]
      : []),
  ];
};

const readCachedLocation = (): LocationInfo | null => {
  try {
    const raw = sessionStorage.getItem(LOCATION_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LocationInfo;
    return parsed?.country_code ? parsed : null;
  } catch {
    return null;
  }
};

const writeCachedLocation = (location: LocationInfo): void => {
  try {
    sessionStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(location));
  } catch {
    /* private mode / quota — detection still works, just uncached */
  }
};

/** Collapses overlapping callers onto the single request already in flight. */
let inFlightDetection: Promise<LocationInfo | null> | null = null;

/**
 * Fetch with a time limit, using AbortController rather than
 * `AbortSignal.timeout` — the latter is missing in JSDOM and older Safari, and
 * throws before the request is even made.
 * @param {string} url - Service URL to call.
 * @returns {Promise<Response>} The response, or a rejection on timeout.
 */
const fetchWithTimeout = async (url: string): Promise<Response> => {
  const controller =
    typeof AbortController === "undefined" ? null : new AbortController();
  const timer = controller
    ? setTimeout(() => controller.abort(), SERVICE_TIMEOUT_MS)
    : undefined;
  try {
    return await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      ...(controller ? { signal: controller.signal } : {}),
    });
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
};

const fetchFromServices = async (): Promise<LocationInfo | null> => {
  for (const serviceUrl of getServiceUrls()) {
    try {
      const response = await fetchWithTimeout(serviceUrl);

      if (!response.ok) {
        continue; // Try next service
      }

      const data = await response.json();

      // Handle different response formats
      const country_code =
        data.country_code || data.country || data.countryCode;
      const country_name = data.country_name || data.country;

      if (country_code) {
        return {
          country_code: country_code,
          country_name: country_name || country_code,
          city: data.city || "",
          region: data.region || data.region_code || data.regionName || "",
          latitude: data.latitude || data.lat || 0,
          longitude: data.longitude || data.lon || data.lng || 0,
          timezone: data.timezone || "",
          currency: data.currency || "",
        };
      }
    } catch (serviceError) {
      console.warn(`Service ${serviceUrl} failed:`, serviceError);
      continue; // Try next service
    }
  }

  console.warn("All IP geolocation services failed, using fallback location");
  return getFallbackLocation();
};

/**
 * Detect the user's location from their IP.
 *
 * Cached per tab session and de-duplicated while in flight, so repeat mounts,
 * re-renders, and page reloads reuse one answer instead of re-running the whole
 * service chain each time.
 * @returns {Promise<LocationInfo | null>} Detected location, or the HK fallback.
 */
export const detectUserLocation = async (): Promise<LocationInfo | null> => {
  const cached = readCachedLocation();
  if (cached) {
    return cached;
  }

  if (inFlightDetection) {
    return inFlightDetection;
  }

  inFlightDetection = (async () => {
    try {
      const location = await fetchFromServices();
      if (location) {
        writeCachedLocation(location);
      }
      return location;
    } catch (error) {
      console.error("Error detecting user location:", error);
      return getFallbackLocation();
    } finally {
      inFlightDetection = null;
    }
  })();

  return inFlightDetection;
};

/** Clear the cached detection and any in-flight request (tests / sign-out). */
export const resetDetectedLocationCache = (): void => {
  inFlightDetection = null;
  try {
    sessionStorage.removeItem(LOCATION_CACHE_KEY);
  } catch {
    /* ignore */
  }
};

export const formatLocationAddress = (locationInfo: LocationInfo): string => {
  const parts = [];

  if (locationInfo.city) {
    parts.push(locationInfo.city);
  }

  if (locationInfo.region && locationInfo.region !== locationInfo.city) {
    parts.push(locationInfo.region);
  }

  if (locationInfo.country_name) {
    parts.push(locationInfo.country_name);
  }

  return parts.join(", ");
};

// Helper function to find matching country from server list
export const findMatchingCountry = (
  locationInfo: LocationInfo,
  serverCountries: any[],
): any | null => {
  if (!locationInfo || !serverCountries || !Array.isArray(serverCountries)) {
    return null;
  }

  // Try to match by ISO2 code first (most reliable)
  let matchedCountry = serverCountries.find(
    (country) =>
      country.iso2?.toLowerCase() === locationInfo.country_code?.toLowerCase(),
  );

  // If no ISO2 match, try by country name
  if (!matchedCountry) {
    matchedCountry = serverCountries.find(
      (country) =>
        country.name?.toLowerCase() ===
        locationInfo.country_name?.toLowerCase(),
    );
  }

  // If still no match, try partial name matching
  if (!matchedCountry && locationInfo.country_name) {
    matchedCountry = serverCountries.find(
      (country) =>
        country.name
          ?.toLowerCase()
          .includes(locationInfo.country_name.toLowerCase()) ||
        locationInfo.country_name
          .toLowerCase()
          .includes(country.name?.toLowerCase()),
    );
  }

  return matchedCountry || null;
};
